import { cast, flow, getParentOfType, getRoot, types } from 'mobx-state-tree'
import { Quiz } from './Quiz'
import { ResourceApi } from '../services/api-objects/ResourceApi'
import { isVimeo, isYoutube } from '../utils/mimetype'

export const Resource = types
  .model('Resource')
  .props({
    audio_file_url: types.maybeNull(types.string),
    transcoding_status: types.frozen(),
    video_file_url: types.maybeNull(types.string),
    created_at: types.maybeNull(types.string),
    converted_pdf_url: types.maybe(types.maybeNull(types.string)),
    quiz_count: types.maybe(types.maybeNull(types.number)),
    thumbnail_url: types.maybe(types.maybeNull(types.string)),
    filename_or_url: types.maybe(types.maybeNull(types.string)),
    language: types.frozen(),
    title: types.string,
    resource_id: types.maybe(types.number),
    source_id: types.number,
    preview_url: types.maybe(types.maybeNull(types.string)),
    is_required: types.maybe(types.boolean),
    raw_text: types.maybeNull(types.string),
    mime_type: types.maybeNull(types.string),
    file_size: types.maybeNull(types.number),
    identityBreaker: types.identifierNumber,
    extension: types.maybeNull(types.string),
    processing_status: types.frozen(),
    editing: types.maybe(types.boolean),
    progress: types.frozen(),
  })
  .actions((self) => ({
    set<K extends keyof typeof self, T extends typeof self>(key: K, value: T[K]) {
      if (key === 'is_required' && value === true) {
        try {
          const parentQuiz = getParentOfType(self, Quiz)
          parentQuiz.resources?.forEach((r) => {
            if (r !== self) {
              if (r.is_required) {
                parentQuiz?.updateRequiredResources(r)
              }
              r.set('is_required', false)
            }
          })
          self[key] = cast(value)
        } catch {
          // do nothing
        }
      } else {
        self[key] = cast(value)
      }
    },
  }))
  .views((self) => ({
    get rootStore(): any {
      return getRoot(self) as any
    },
    get isYoutube() {
      const reg =
        /^((?:https?:)?\/\/)?((?:www|m)\.)?((?:youtube\.com|youtu.be))(\/(?:[\w\-]+\?v=|embed\/|v\/)?)([\w\-]+)(\S+)?$/
      if (reg.test(self.filename_or_url || '')) {
        return true
      }
      return isYoutube(self.filename_or_url || '')
    },
    get isVimeo() {
      return isVimeo(self.filename_or_url || '')
    },
    get isVideo() {
      const { utilityStore } = this.rootStore
      const { videoMimetypes } = utilityStore
      return this.isYoutube || this.isVimeo || videoMimetypes.includes(self.mime_type)
    },
    get isImage() {
      const { utilityStore } = this.rootStore
      const { imageMimetypes } = utilityStore
      return imageMimetypes.includes(self.mime_type)
    },
    get isAudio() {
      const { utilityStore } = this.rootStore
      const { audioMimetypes } = utilityStore
      return audioMimetypes.includes(self.mime_type)
    },
    get isMercuryEnabled() {
      const { utilityStore } = this.rootStore
      const { mercuryMimetypes } = utilityStore
      return (
        this.isYoutube ||
        this.isVimeo ||
        mercuryMimetypes.includes(self.mime_type) ||
        self.mime_type === null
      )
    },
  }))
  .actions((self) => ({
    refreshURL: flow(function* () {
      try {
        ResourceApi.getSourceDetails(self.source_id).then((resp) => {
          if (resp.ok) {
            const { filename_or_url } = resp.data as any
            self.set('filename_or_url', filename_or_url)
          } else {
            return false
          }
        })
      } catch (error) {
        return false
      }
    }),
  }))
